import { BIND_SOURCES, formatMAD, formatMoney, menuHandleOf, type SocialNetwork } from '@xshop/shared';
import { reviewFact, type ReviewFacts } from './reviewFacts';
import { contactFact, isStoreDetail, type ContactFacts } from './storeContact';
import { plainText, summaryOf } from './plainText';

// What the shop can answer a bound prop with. The shell has already resolved every one of these by the
// time a page draws, so binding a field costs no read and cannot make a header wait on the network.
//
// Deliberately plain values: an element reads a fact, never the shell's own state or callbacks. That is
// what lets the same tree draw inside the builder, where there is no shell, against nothing at all.

/** The product this page is about, when it is about one. */
export type ProductFacts = {
  title: string;
  href?: string;
  hoverImage?: string;
  type?: string;
  badge?: string;
  price: number;
  compareAtPrice: number | null;
  description: string;
  image: string;
  /** Units the shopper can order now, or null when the shop does not count them. */
  stock?: number | null;
  /** The approved-review average, 0 to 5, and how many reviews made it. */
  rating?: number;
  ratingCount?: number;
  /** How much below its compare-at price it sells, in whole percent; 0 when it is not on sale. */
  discountPercent?: number;
  /** True for a product added in the last thirty days, which the card marks as new. */
  isNew?: boolean;
};

/** Everything a binding may name, in the shape the shell already holds it. */
export type StoreFacts = {
  collection?: { title: string; image: string; href: string; count: string; description?: string };
  storeName: string;
  /** The picture the merchant uploaded as their logo, or nothing while they have not. */
  logoUrl: string;
  currency: string;
  moneyLocale: 'ar-MA' | 'fr-MA';
  cartCount: number;
  cartTotal: number;
  product: ProductFacts | null;
  /** The language the store chose to be served in, when it chose one; the document is marked with it. */
  language?: 'ar' | 'fr' | 'en';
  /** Which way the store's pages read, from its language, else its theme's declared direction. */
  direction?: 'ltr' | 'rtl';
  /** The page the merchant wrote, on a page reached at /pages/<handle>, or what to say when it is not there. */
  page?: { title: string; body: string };
  /** What the shopper searched for, on the search page; empty when they have not typed anything yet. */
  search?: { query: string };
  /** The store's social profiles that have an address, in the order a row of icons draws them. */
  social?: ReadonlyArray<{ network: SocialNetwork; url: string }>;
  /** The review a card inside a Reviews list is drawing. */
  review?: ReviewFacts;
  /** How the store is reached, from its settings; nothing where the page was drawn without them. */
  contact?: ContactFacts;
};

/** What a bound prop resolves to: text for most, a whole number for a count, nothing when unanswerable. */
export type BoundValue = string | number | undefined;

// Spells an amount the way the rest of this storefront spells one — dirhams natively, anything else by Intl.
export const money = (amount: number, facts: StoreFacts): string =>
  facts.currency.toUpperCase() === 'MAD' ? formatMAD(amount, facts.moneyLocale) : formatMoney(amount, facts.currency, facts.moneyLocale);

/** The words of the card's badges in each language a store may be served in; `off` words a discount. */
const BADGE_WORDS: Readonly<Record<'ar' | 'fr' | 'en', { new: string; soldOut: string; off: (percent: number) => string }>> = {
  en: { new: 'New', soldOut: 'Sold out', off: (percent) => `${percent}% OFF` },
  fr: { new: 'Nouveau', soldOut: 'Épuisé', off: (percent) => `-${percent} %` },
  ar: { new: 'جديد', soldOut: 'نفد', off: (percent) => `خصم ${percent}%` }
};

// Words the product's one badge in the store's language: its discount, else that it is new, else nothing.
const productBadge = (product: ProductFacts, facts: StoreFacts): string => {
  const words = BADGE_WORDS[facts.language ?? 'en'];
  return product.discountPercent ? words.off(product.discountPercent) : product.isNew ? words.new : '';
};

// Words one badge in the store's language, or the empty string that hides it.
const badgeWord = (show: boolean, facts: StoreFacts, word: 'new' | 'soldOut'): string =>
  (show ? BADGE_WORDS[facts.language ?? 'en'][word] : '');

/** How each product source is answered from the product's facts. */
const PRODUCT_FACTS: Readonly<Record<string, (product: ProductFacts, facts: StoreFacts) => BoundValue>> = {
  'product.hoverImage': (product) => product.hoverImage ?? '',
  'product.type': (product) => product.type ?? '',
  'product.badge': (product, facts) => productBadge(product, facts),
  'product.href': (product) => product.href,
  'product.title': (product) => product.title,
  'product.price': (product, facts) => money(product.price, facts),
  'product.description': (product) => plainText(product.description),
  'product.summary': (product) => summaryOf(product.description),
  'product.image': (product) => (product.image === '' ? undefined : product.image),
  'product.compareAtPrice': (product, facts) => (product.compareAtPrice === null ? '' : money(product.compareAtPrice, facts)),
  'product.stock': (product, facts) => stockLine(product.stock, facts),
  'product.rating': (product) => product.rating ?? 0,
  'product.ratingCount': (product) => (product.ratingCount ? `(${product.ratingCount})` : ''),
  'product.saleBadge': (product) => (product.discountPercent ? `-${product.discountPercent}%` : ''),
  'product.newBadge': (product, facts) => badgeWord(Boolean(product.isNew), facts, 'new'),
  'product.soldOutBadge': (product, facts) => badgeWord(product.stock === 0, facts, 'soldOut')
};

/** The stock line's words in each language a store is served in: how many are left, and none. */
const STOCK_WORDS: Readonly<Record<'ar' | 'fr' | 'en', readonly [(count: number) => string, string]>> = {
  en: [(count) => `${count} in stock`, 'Out of stock'],
  fr: [(count) => `${count} en stock`, 'Épuisé'],
  ar: [(count) => `${count} متوفر`, 'نفد المخزون']
};

// Says how many units can be ordered, the way Sleek's product page does: "32 in stock", or that none can.
function stockLine(stock: number | null | undefined, facts: StoreFacts): string {
  if (stock === null || stock === undefined) return '';
  const [left, none] = STOCK_WORDS[facts.language ?? 'en'];
  return stock > 0 ? left(stock) : none;
}

/** The search page's heading in each language a store is served in: with the words searched, and without. */
const SEARCH_TITLES: Readonly<Record<'ar' | 'fr' | 'en', readonly [(query: string) => string, string]>> = {
  en: [(query) => `Search results for “${query}”`, 'Search our store'],
  fr: [(query) => `Résultats pour « ${query} »`, 'Rechercher dans la boutique'],
  ar: [(query) => `نتائج البحث عن «${query}»`, 'ابحث في المتجر']
};

// Says what the search page is showing, in the store's language, or nothing on any other page.
function searchTitle(facts: StoreFacts): BoundValue {
  if (!facts.search) return undefined;
  const [found, idle] = SEARCH_TITLES[facts.language ?? 'en'];
  return facts.search.query === '' ? idle : found(facts.search.query);
}

/** How each source about the shop itself, its cart or the page is answered from the facts. */
const SHOP_FACTS: Readonly<Record<string, (facts: StoreFacts) => BoundValue>> = {
  'store.name': (facts) => facts.storeName,
  'store.logo': (facts) => (facts.logoUrl === '' ? undefined : facts.logoUrl),
  'store.copyright': (facts) => `© ${new Date().getFullYear()} ${facts.storeName}`.trim(),
  'cart.count': (facts) => facts.cartCount,
  'cart.total': (facts) => money(facts.cartTotal, facts),
  'page.title': (facts) => facts.page?.title,
  'page.body': (facts) => facts.page?.body,
  'search.title': searchTitle
};

// Answers a product source, or nothing at all on a page that is not about a product.
function productFact(source: string, product: ProductFacts | null, facts: StoreFacts): BoundValue {
  if (!product) return undefined;
  return PRODUCT_FACTS[source]?.(product, facts);
}

/**
 * Answers one binding from what the shop already knows.
 *
 * A menu resolves to nothing here on purpose — it is read by handle and arrives late, so the element
 * that binds one owns both the read and the gap before it lands. Everything else is answered now or not
 * at all, and "not at all" means the element keeps whatever the merchant last typed rather than blanking.
 */
export function resolveBinding(source: string, facts: StoreFacts | null): BoundValue {
  if (facts === null || menuHandleOf(source) !== null) return undefined;
  if (!(BIND_SOURCES as readonly string[]).includes(source)) return undefined;
  if (source.startsWith('collection.')) return facts.collection?.[source.slice(11) as keyof NonNullable<StoreFacts['collection']>];
  if (source.startsWith('review.')) return reviewFact(source, facts);
  if (isStoreDetail(source)) return contactFact(source, facts.contact);
  const shopFact = SHOP_FACTS[source];
  return shopFact ? shopFact(facts) : productFact(source, facts.product, facts);
}
